import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Pagination, Badge, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { unregisteredUserVisitService } from '../services/apiService';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useAuthToken } from '../hooks/useAuthToken';
import StackedTable from '../components/StackedTable';
import ReportHeader from '../components/ReportHeader';
import { LoadingState, ErrorState } from '../components/PageState';
import { EMPTY, formatDateTime, formatNumber, plural } from '../utils/format';

/** The select's values as the API's newDomain filter. */
const DOMAIN_FILTERS = {
  all: null,
  new: true,
  existing: false,
};

const domainOf = (email) => (email && email.includes('@') ? email.slice(email.lastIndexOf('@') + 1).toLowerCase() : null);

/**
 * People who opened Team Today inside Microsoft Teams while signed out, with a Teams address that has
 * no Team Today account. The app logs each address once, on its first visit, and notes whether
 * anyone at that email domain already uses Team Today.
 */
const UnregisteredUserVisits = () => {
  const refreshToken = useAuthToken();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(null);
  const [domainFilter, setDomainFilter] = useState('all');
  const latestRequest = useRef(0);
  const isMobile = useIsMobile();
  const pageSize = 50;

  useEffect(() => {
    fetchVisits(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, domainFilter]);

  const fetchVisits = async (page) => {
    // Only the newest request may update the table, so a slow earlier page cannot overwrite it
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    try {
      setLoading(true);
      setError(null);
      await refreshToken();
      const response = await unregisteredUserVisitService.getVisits(page, pageSize, DOMAIN_FILTERS[domainFilter]);
      if (request !== latestRequest.current) return;
      setVisits(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (err) {
      if (request !== latestRequest.current) return;
      setError('Could not load the visits.');
      console.error(err);
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (value) => {
    setCurrentPage(0);
    setDomainFilter(value);
  };

  const renderPagination = () => {
    if (error || totalPages <= 1) return null;

    const displayPage = currentPage + 1;
    // Fewer page numbers either side of the current one on phones, so the bar fits on one line
    const neighbours = isMobile ? 1 : 2;

    return (
      <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <small className="text-muted">
          Showing {formatNumber(currentPage * pageSize + 1)} to {formatNumber(Math.min((currentPage + 1) * pageSize, totalElements))} of {formatNumber(totalElements)}
        </small>
        <Pagination className="mb-0">
          <Pagination.Prev
            disabled={currentPage === 0}
            onClick={() => handlePageChange(currentPage - 1)}
          />
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(page => page === 1 || page === totalPages || Math.abs(page - displayPage) <= neighbours)
            .reduce((items, page, idx, arr) => {
              if (idx > 0 && page - arr[idx - 1] > 1) {
                items.push(<Pagination.Ellipsis key={`ellipsis-${page}`} disabled />);
              }
              items.push(
                <Pagination.Item
                  key={page}
                  active={page === displayPage}
                  onClick={() => handlePageChange(page - 1)}
                >
                  {page}
                </Pagination.Item>
              );
              return items;
            }, [])}
          <Pagination.Next
            disabled={currentPage === totalPages - 1}
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </Pagination>
      </Card.Footer>
    );
  };

  const renderBody = () => {
    if (error) return <ErrorState inCard message={error} onRetry={() => fetchVisits(currentPage)} />;
    if (loading && visits.length === 0) return <LoadingState label="Loading visits" />;
    if (visits.length === 0) {
      return (
        <div className="text-center text-muted p-4">
          {domainFilter === 'all' ? 'No visits logged yet' : `No visits from ${domainFilter} domains`}
        </div>
      );
    }
    return (
      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 120ms ease-out' }} aria-busy={loading}>
        <StackedTable responsive hover className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Email</th>
              <th>Domain</th>
              <th>First visit</th>
            </tr>
          </thead>
          <tbody>
            {visits.map((visit) => {
              const domain = domainOf(visit.email);
              return (
                <tr key={visit.id}>
                  <td className="align-middle">{visit.email || EMPTY}</td>
                  <td className="align-middle">
                    {!domain ? EMPTY : visit.newDomain ? (
                      <>
                        {domain}
                        <Badge bg="primary" className="ms-2">New</Badge>
                      </>
                    ) : (
                      <Link
                        to={{ pathname: '/user-search', search: `?q=${encodeURIComponent(`@${domain}`)}` }}
                        title={`Find Team Today users at ${domain}`}
                      >
                        {domain}
                      </Link>
                    )}
                  </td>
                  <td className="align-middle" style={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(visit.createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </StackedTable>
      </div>
    );
  };

  return (
    <Container fluid className="py-4">
      <ReportHeader
        title="Unregistered user visits"
        description="People who opened Team Today in Microsoft Teams with an email address that has no Team Today account. Each address is logged once, on its first visit."
      />

      <p className="text-muted small mb-4">
        New domain: nobody with that email domain had a Team Today account when the visit was logged, so it may be a new customer.
        Any other domain already has users, and links to them so you can see which company the visitor probably belongs to.
      </p>

      <Card className="shadow-sm">
        <Card.Header className="bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">Visits</h5>
          <div className="d-flex align-items-center flex-wrap gap-3">
            {totalElements != null && !error && (
              <span className="small text-muted">{plural(totalElements, 'email address', 'email addresses')}</span>
            )}
            <Form.Group controlId="unregisteredDomainFilter" className="d-flex align-items-center gap-2">
              <Form.Label className="small text-muted mb-0">Domain</Form.Label>
              <Form.Select size="sm" value={domainFilter} onChange={(e) => handleFilterChange(e.target.value)} style={{ width: 'auto' }}>
                <option value="all">All domains</option>
                <option value="new">New domains</option>
                <option value="existing">Existing domains</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {renderBody()}
        </Card.Body>
        {renderPagination()}
      </Card>
    </Container>
  );
};

export default UnregisteredUserVisits;
